import React from 'react';
import { ENV_CONFIG } from '../constants';
import { 
  WrenchScrewdriverIcon, 
  ClockIcon, 
  EnvelopeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const MaintenancePage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-700 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          {/* Icon */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-orange-500/20 rounded-full mb-6 border border-orange-500/30">
              <WrenchScrewdriverIcon className="w-12 h-12 text-orange-400" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 font-display">
              Under Maintenance
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 font-light">
              {ENV_CONFIG.siteName} - {ENV_CONFIG.siteTagline}
            </p>
          </div>

          {/* Maintenance Message */}
          <div className="mb-16">
            <div className="inline-flex items-center bg-orange-500/20 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-orange-500/30">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-400 mr-3" />
              <span className="text-orange-200 font-medium">Scheduled Maintenance</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              We'll Be Back Soon
            </h2>
            
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              We're currently performing scheduled maintenance to improve your experience. 
              Our team is working hard to bring the site back online as quickly as possible.
            </p>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 mb-8">
              <div className="flex items-center justify-center mb-4">
                <ClockIcon className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-300 font-medium">Estimated Time</span>
              </div>
              <p className="text-2xl font-bold text-white">
                2-4 Hours
              </p>
              <p className="text-gray-400 text-sm mt-2">
                We apologize for any inconvenience
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50">
            <h3 className="text-2xl font-bold text-white mb-4">
              Need Immediate Assistance?
            </h3>
            <p className="text-gray-300 mb-6">
              For urgent matters, please contact us directly:
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-gray-300">
              <a
                href="mailto:support@csean.org.ng"
                className="inline-flex items-center hover:text-white transition-colors"
              >
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                support@csean.org.ng
              </a>
              <span className="hidden sm:block">•</span>
              <a
                href="tel:+2341234567890"
                className="inline-flex items-center hover:text-white transition-colors"
              >
                <span className="w-5 h-5 mr-2 text-center">📞</span>
                +234 (703) 930 6310
              </a>
            </div>
          </div>

          {/* Status Updates */}
          <div className="mt-12">
            <p className="text-gray-400 text-sm mb-4">
              Follow us for updates on the maintenance progress:
            </p>
            <div className="flex justify-center space-x-6">
              <a
                href="https://twitter.com/csean_ng"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Twitter
              </a>
              <a
                href="https://linkedin.com/company/csean"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
        <p className="text-gray-400 text-sm">
          © {currentYear} {ENV_CONFIG.siteName}. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
